/**
 * Proyecto: MOGOTAX-SERVER
 * Autor: Megatrack GPS SAS
 * Fecha de creación: 11 de noviembre 2024
 * Licencia: Propiedad Privada
 *
 * Este código es propiedad exclusiva de Megatrack GPS SAS & Mogotax SAS y no puede ser distribuido, copiado, modificado, publicado, sublicenciado ni comercializado sin el consentimiento expreso y por escrito de ambas empresa.
 *
 * Copyright (c) 2025 Megatrack GPS SAS. Todos los derechos reservados.
 */
import React, {
  createContext,
  useState,
  useContext,
  useMemo,
  useCallback,
  useEffect,
} from "react";
import PropTypes from "prop-types";
import { useSocket } from "./SocketContext";
import { useUser } from "./UserContext";
import { message } from "antd";

// Los eventos WebSocket traen el traslado sin relaciones: se conservan conductor y vehículo (los muestra
// el administrador de vehículos) mientras sigan siendo los mismos.
const conservarRelaciones = (anterior, nuevo) => ({
  ...nuevo,
  conductor:
    nuevo.conductor ??
    (anterior?.conductor && Number(anterior.conductor.id) === Number(nuevo.conductor_id)
      ? anterior.conductor
      : undefined),
  vehiculo:
    nuevo.vehiculo ??
    (anterior?.vehiculo && Number(anterior.vehiculo.id) === Number(nuevo.vehiculo_id)
      ? anterior.vehiculo
      : undefined),
});

const TrasladosContext = createContext();

export const useTraslados = () => {
  return useContext(TrasladosContext);
};

export const TrasladosProvider = ({ children }) => {
  const [traslados, setTraslados] = useState([]);
  const { user } = useUser();
  const sesion = user ? `${user.role || "conductor"}:${user.id}` : null;

  // Otro usuario u otro perfil (conductor ↔ administrador): la lista anterior no le corresponde
  useEffect(() => {
    setTraslados([]);
  }, [sesion]);
  const {
    onTrasladoUpdate,
    onTrasladoActualizado,
    onTrasladoStatusChange,
    onTrasladoAsignado,
    onTrasladoCancelado,
    socketConnected,
  } = useSocket();

  const replaceTraslados = useCallback((nuevosTraslados) => {
    setTraslados(nuevosTraslados);
  }, []);

  const setTrasladosMerge = useCallback((nuevosTraslados) => {
    setTraslados((prevTraslados) => {
      const trasladosMap = new Map();
      prevTraslados.forEach((traslado) => {
        trasladosMap.set(traslado.id, traslado);
      });
      nuevosTraslados.forEach((traslado) => {
        trasladosMap.set(traslado.id, traslado);
      });
      return Array.from(trasladosMap.values());
    });
  }, []);

  const addTraslado = useCallback((nuevoTraslado) => {
    setTraslados((prevTraslados) => {
      const existe = prevTraslados.some((t) => t.id === nuevoTraslado.id);
      if (!existe) {
        return [...prevTraslados, nuevoTraslado];
      } else {
        return prevTraslados.map((t) =>
          t.id === nuevoTraslado.id ? nuevoTraslado : t
        );
      }
    });
  }, []);

  const updateTraslado = useCallback((trasladoActualizado) => {
    setTraslados((prevTraslados) =>
      prevTraslados.map((traslado) =>
        traslado.id === trasladoActualizado.id
          ? conservarRelaciones(traslado, trasladoActualizado)
          : traslado
      )
    );
  }, []);

  const removeTraslado = useCallback((trasladoId) => {
    setTraslados((prevTraslados) =>
      prevTraslados.filter((traslado) => traslado.id !== trasladoId)
    );
  }, []);

  // Suscribirse a eventos WebSocket para actualizaciones en tiempo real
  useEffect(() => {
    if (!socketConnected) return;

    // Actualización simple de traslado
    const unsubUpdate = onTrasladoUpdate((data) => {
      console.log('📡 WS: Traslado actualizado', data);
      if (data.traslado) {
        updateTraslado(data.traslado);
      } else if (data.trasladoId && data.changes) {
        // Actualización parcial
        setTraslados((prev) =>
          prev.map((t) =>
            t.id === data.trasladoId ? { ...t, ...data.changes } : t
          )
        );
      }
    });

    // Traslado actualizado completamente
    const unsubActualizado = onTrasladoActualizado(({ traslado }) => {
      console.log('📡 WS: Traslado actualizado completo', traslado?.id);
      if (traslado) {
        updateTraslado(traslado);
      }
    });

    // Cambio de estado
    const unsubStatus = onTrasladoStatusChange(({ traslado }) => {
      console.log('📡 WS: Cambio de estado', traslado?.id, traslado?.status);
      if (traslado) {
        updateTraslado(traslado);
      }
    });

    // Nuevo traslado asignado
    const unsubAsignado = onTrasladoAsignado(({ traslado }) => {
      console.log('🚀 WS: Nuevo traslado asignado', traslado?.id);
      if (traslado) {
        addTraslado(traslado);
        message.success({
          content: `Nuevo traslado asignado: ${traslado.origen_lugar || 'Sin origen'} → ${traslado.destino_lugar || 'Sin destino'}`,
          duration: 5,
        });
      }
    });

    // Traslado cancelado
    const unsubCancelado = onTrasladoCancelado(({ traslado }) => {
      console.log('❌ WS: Traslado cancelado', traslado?.id);
      if (traslado) {
        updateTraslado(traslado);
        message.warning({
          content: `Traslado cancelado: ${traslado.origen_lugar || 'Sin origen'}`,
          duration: 5,
        });
      }
    });

    // Cleanup
    return () => {
      if (typeof unsubUpdate === 'function') unsubUpdate();
      if (typeof unsubActualizado === 'function') unsubActualizado();
      if (typeof unsubStatus === 'function') unsubStatus();
      if (typeof unsubAsignado === 'function') unsubAsignado();
      if (typeof unsubCancelado === 'function') unsubCancelado();
    };
  }, [
    socketConnected,
    onTrasladoUpdate,
    onTrasladoActualizado,
    onTrasladoStatusChange,
    onTrasladoAsignado,
    onTrasladoCancelado,
    updateTraslado,
    addTraslado,
  ]);

  const value = useMemo(
    () => ({
      traslados,
      replaceTraslados,
      setTrasladosMerge,
      addTraslado,
      updateTraslado,
      removeTraslado,
    }),
    [
      traslados,
      replaceTraslados,
      setTrasladosMerge,
      addTraslado,
      updateTraslado,
      removeTraslado,
    ]
  );

  return (
    <TrasladosContext.Provider value={value}>
      {children}
    </TrasladosContext.Provider>
  );
};

TrasladosProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
